import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import { useNavigate } from "react-router-dom";
import styles from "./Overview.module.sass";
import Card from "../../../components/Card";
import Icon from "../../../components/Icon";
import Tooltip from "../../../components/Tooltip";
import TooltipGlodal from "../../../components/TooltipGlodal";
import { getCompanyForCurrentUser } from "../../../services/company.service";
import { getFoldersByCompanyId } from "../../../services/folder.service";
import { getCompanyOffers } from "../../../services/offer.service";
import { getUnreadNotificationsCount } from "../../../services/notification.service";
import { pb } from "../../../services/pocketbase";

const getFoldersStatus = (folders) => {
  if (!folders || folders.length === 0) {
    return {
      counter: "0",
      statusColor: "purple",
      statusText: "Aucun dossier",
      tooltip: "Aucun dossier d'alternance n'est associé à votre entreprise pour le moment."
    };
  }

  // Compter les dossiers à signer (entreprise)
  const toSign = folders.filter(f => {
    const companySigned = f.progression?.entreprise?.sign;
    const cerfaGenerated = f.progression?.ecole?.cerfa_genere;
    return cerfaGenerated && !companySigned;
  }).length;

  if (toSign > 0) {
    return {
      counter: folders.length.toString(),
      statusColor: "red",
      statusText: `${toSign} à signer`,
      tooltip: `Vous avez ${toSign} contrat(s) en attente de votre signature.`
    };
  }

  // Compter les dossiers en cours
  const inProgress = folders.filter(f => {
    const allSigned = f.progression?.etudiant?.sign && 
                      f.progression?.entreprise?.sign && 
                      f.progression?.ecole?.sign;
    return !allSigned;
  }).length;

  if (inProgress > 0) {
    return {
      counter: folders.length.toString(),
      statusColor: "purple",
      statusText: `${inProgress} en cours`,
      tooltip: `${inProgress} dossier(s) sont en cours de traitement.`
    };
  }

  return {
    counter: folders.length.toString(),
    statusColor: "green",
    statusText: "Tous validés",
    tooltip: "Tous vos dossiers d'alternance sont complets et validés."
  };
};

const getOffersStatus = (offersData) => {
  if (!offersData || offersData.length === 0) {
    return {
      counter: "0",
      statusColor: "purple",
      statusText: "Aucune offre",
      tooltip: "Vous n'avez pas encore publié d'offre d'alternance."
    };
  }

  const activeOffers = offersData.filter(o => o.status === 'Active').length;

  return {
    counter: offersData.length.toString(),
    statusColor: activeOffers > 0 ? "green" : "purple",
    statusText: activeOffers > 0 ? `${activeOffers} active(s)` : "Aucune active",
    tooltip: activeOffers > 0 
      ? `Vous avez ${activeOffers} offre(s) d'alternance active(s).`
      : "Aucune offre active pour le moment."
  };
};

const getBaseItems = (foldersStatus, offersStatus, unreadNotificationsCount) => {
  const hasUnreadNotifications = unreadNotificationsCount > 0;

  return [
    {
      title: "Mes dossiers",
      counter: foldersStatus.counter,
      icon: "folder",
      background: "#edf8f2",
      statusColor: foldersStatus.statusColor,
      statusText: foldersStatus.statusText,
      tooltip: foldersStatus.tooltip,
      route: "/company/contracts"
    },
    {
      title: "Mes offres",
      counter: offersStatus.counter,
      icon: "diamond",
      background: "#ecf9fe",
      statusColor: offersStatus.statusColor,
      statusText: offersStatus.statusText,
      tooltip: offersStatus.tooltip,
      route: "/company/alternance-offers"
    },
    {
      title: "Notifications",
      counter: unreadNotificationsCount > 0 ? unreadNotificationsCount.toString() : "0",
      icon: "notification",
      background: "#f2efff",
      statusColor: hasUnreadNotifications ? "purple" : "green",
      statusText: hasUnreadNotifications ? "À lire" : "Aucune nouvelle",
      tooltip: hasUnreadNotifications 
        ? `Vous avez ${unreadNotificationsCount} nouvelle(s) notification(s).`
        : "Aucune nouvelle notification.",
      route: "/company/notifications"
    },
  ];
};

const Overview = ({ className, currentUser }) => {
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [offers, setOffers] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      // Récupérer l'entreprise connectée
      const company = await getCompanyForCurrentUser();
      
      if (company?.id) {
        // Récupérer les dossiers de l'entreprise
        const companyFolders = await getFoldersByCompanyId(company.id);
        setFolders(companyFolders || []);
      }

      // Récupérer les offres de l'entreprise
      const offersResult = await getCompanyOffers({ perPage: 100 });
      if (offersResult.success) {
        setOffers(offersResult.data || []);
      }

      // Récupérer les notifications non lues
      const notificationsCount = await getUnreadNotificationsCount();
      setUnreadNotificationsCount(notificationsCount);

    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    // Listeners temps réel
    const setupRealtimeListeners = () => {
      pb.collection('folders').subscribe('*', () => fetchData());
      pb.collection('alternance_offers').subscribe('*', () => fetchData());
      pb.collection('notifications').subscribe('*', () => fetchData());
    };

    setupRealtimeListeners();

    return () => {
      pb.collection('folders').unsubscribe();
      pb.collection('alternance_offers').unsubscribe();
      pb.collection('notifications').unsubscribe();
    };
  }, [fetchData]);

  const handleItemClick = (item) => {
    if (item.route) {
      navigate(item.route);
    }
  };

  const foldersStatus = getFoldersStatus(folders);
  const offersStatus = getOffersStatus(offers);
  const items = getBaseItems(foldersStatus, offersStatus, unreadNotificationsCount);

  return (
    <>
      <Card
        className={cn(styles.card, className)}
        title={`Votre tableau de bord, ${currentUser?.first_name || 'Utilisateur'}`}
        classTitle="title-yellow"
      >
        <div className={styles.overview}>
          <div className={styles.list}>
            {loading ? (
              <div>Chargement...</div>
            ) : (
              items.map((x, index) => (
                <div
                  className={cn(styles.item, { [styles.clickable]: x.route })}
                  key={index}
                  style={{ backgroundColor: x.background }}
                  onClick={() => handleItemClick(x)}
                >
                  <div className={styles.icon}>
                    <Icon name={x.icon} size="24" />
                  </div>
                  <div className={styles.line}>
                    <div className={styles.details}>
                      <div className={styles.category}>
                        {x.title}
                        <Tooltip
                          className={styles.tooltip}
                          title={x.tooltip}
                          icon="info"
                          place="right"
                        />
                      </div>
                      <div className={styles.counter}>{x.counter}</div>
                      <div className={styles.indicator}>
                        {x.statusText && (
                          <div
                            className={cn(
                              { "status-purple": x.statusColor === "purple" },
                              { "status-green-dark": x.statusColor === "green" },
                              { "status-red-dark": x.statusColor === "red" },
                              styles.status
                            )}
                          >
                            {x.statusText}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Card>
      <TooltipGlodal />
    </>
  );
};

export default Overview;
